<template>
  <div class="qb-result">

    <div class="flex justify-between shadow-sm">

      <div class="flex items-center justify-start flex-grow">
        <transition name="fade" :duration="200">
          <div v-if="showArticleList === false" class="qb-results-pagination">
            <span>{{$t('results_pagination.limit')}}: </span>
            <input-select
              :selectedValue="query.limit"
              class="qb-results-limit"
              :options="limitOptions"
              :onChange="(value) => onResultsPaginationChange('limit', value)"
              :allowEmpty="false"
              :preselectFirst="true"
            />
          </div>
        </transition>

        <span class="ml10 text-sm text-required">{{runErrorMessage}}</span>
      </div>

      <span v-if="showArticleList && (required.rows)" class="text-required">{{$t('save.rowsRequired')}}</span>

      <div class="qb-query-icons align-right">
        <span class="fas"
              :class="{'fa-table': showArticleList === true, 'fa-bars': showArticleList === false, disabled: !(this.queryResult && this.queryResult.data && this.queryResult.data.data && this.queryResult.data.data.length)}"
              @click="onShowList">
        </span>
        
        <div class="qb-export-wrapper">
          <app-dropdown-button
              :options="exportOptions"
              icon="fas fa-download"
              :customCss="`qb-export-dropdown ${!queryResultHasData ? 'disabled' : ''}`"
              @article="(e) => onExport(e, 'article')"
              @tag="(e) => onExport(e, 'tag')"
              @source="(e) => onExport(e, 'source')"
              :tooltip="$t('exports.youtube_restriction')"
          >
            <li slot="header" class="qb-export-fulltext" @click.stop>
              <label>
                <input type="checkbox" v-model="includeFulltext" @click.stop>
                {{ $t('exports.include_fulltext') }}
              </label>
            </li>
            <li>
              <CopyTableData :rows="rows" />
            </li>
          </app-dropdown-button>
        </div>
        
        <span v-if="runIsBusy" class="spinner fa-spin"></span>
        <span v-else class="fas fa-play"
              @click="() => onRun({pagination: {currentPage: 1}})">
        </span>
      </div>
    </div>

    <app-articles-table v-if="showArticleList"
      :query="query"
      :articlesPagination="articlesTablePagination"
      @setArticlesPagination="onArticlesTablePaginationChange"
    />

    <app-result-table v-else
                      :query="query"
                      :columns="query.columns"
                      :rows="query.rows"
                      :required="required"
                      :blocked="blocked"
                      :queryOutputs="query.outputs"
                      :queryResult="queryResult"
                      :queryPagination="queryPagination"
                      :onDrop="onDrop"
                      :dragging="dragging"
                      :outputs="settings.data && settings.data.outputs"
                      :localTextOptions="localTextOptions"
                      :queryTablePagination="queryResultPagination"
                      @setQueryTablePagination="onQueryTablePaginationChange"
                      @onPaginationChange="onResultsPaginationChange"
                      @setQueryEntity="$emit('setQueryEntity', $event)"
                      @removeElement="$emit('removeElement', $event)"
                      @onShowArticleList="onShowArticleList"
                      @onRun="onRun"
    />

    <small v-if="!showArticleList && projectLastUpdate && query.limit !== -1" class="updated-at">
      {{ $t("updated_at") }} {{ projectLastUpdate }}
    </small>

    <div v-if="showArticleList && articlesTablePagination" class="qb-articles-table-pagination">
      <pagination :isLoading="articlesIsBusy" :pagination="articlesTablePagination" @onPaginationChange="onArticlesTablePaginationChange" />
    </div>
    <div v-if="!showArticleList && queryResultPagination && query.limit === -1" class="qb-query-table-pagination">
      <pagination 
        :isLoading="runIsBusy"
        :showTotalPages="true"
        :pagination="queryResultPagination" 
        :perPageOptions="queryPerPageOptions" 
        @onPaginationChange="onQueryTablePaginationChange"
      />
      <small v-if="!showArticleList && projectLastUpdate" class="updated-at">
        {{ $t("updated_at") }} {{ projectLastUpdate }}
      </small>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import { get } from 'lodash';

  import ResultTable from './result_table';
  import ArticlesTable from './articles_table';
  import Pagination from '../pagination';
  import InputSelect from '../form-components/input-select';
  import DropdownButton from '../dropdown-button';
  import CopyTableData from '../copy-table-data';
  import { CATEGORY_TYPES } from "../../config/const";

  export default {
    props: {
      query: {
        type: Object,
        required: () => true
      },
      queryResult: {
        type: Object,
        default: () => {}
      },
      required: {
        type: Object,
        default: () => {}
      },
      blocked: {
        type: Object,
        default: () => {}
      },
      onDrop: {
        type: Function,
        default: () => null
      },
      dragging: {
        type: Boolean,
        default: () => false
      },
      localTextOptions: {
        type: Array,
        default: () => []
      },
      localHeadlineOptions: {
        type: Array,
        default: () => []
      },
      showArticleList: {
        type: Boolean,
        default: () => false
      },
      articlesTablePagination: {
        type: Object,
        required: () => true
      },
      queryResultPagination: {
        type: Object,
        required: () => false
      }
    },
    components: {
      'app-result-table': ResultTable,
      'app-articles-table': ArticlesTable,
      'pagination': Pagination,
      'input-select': InputSelect,
      'app-dropdown-button': DropdownButton,
      CopyTableData
    },
    data: function () {
      return {
        resultsLimit: null,
        showExportOptions: false,
        includeFulltext: false,
        queryPerPageOptions: [
          { label: 300, value: 300 },
          { label: 500, value: 500 },
        ]
      }
    },
    computed: {
      ...mapState({
        settings: state => state.queryBuilder.settings.settings,
        runError: state => state.queryBuilder.query.run.error,
        runIsBusy: state => state.queryBuilder.query.run.isBusy,
        articlesIsBusy: state => state.queryBuilder.articles.articles.isBusy,
      }),

      ...mapGetters({
        projectLastUpdate: 'projects/projectLastUpdate',
      }),

      runErrorMessage() {
        return this.runError && this.$i18n.t('messages.queries.run.error');
      },

      limitOptions() {
        return [
          { value: 5, label: '5' },
          { value: 10, label: '10' },
          { value: 25, label: '25' },
          { value: 50, label: '50' },
          { value: 100, label: '100' },
          { value: 200, label: '200' },
          { value: -1, label: 'All' }
        ]
      },

      orderOptions() {
        if(this.settings && this.settings.data && this.settings.data.outputs && this.query.outputs) {
          return this.query.outputs.map(output => ({ value: output, label: this.getOutputById(output).name }))
        }

        return [];
      },
      queryPagination () {
        let query = {...{perPage: this.query.limit}}
        if(this.query.order) this.$set(query, 'order', this.query.order)
        if(this.query.sort) this.$set(query, 'sort', this.query.sort)
        return query
      },
      exportOptions() {
        const tagLevelEnabled = this.query.rows.find(r => r.type == "source" || r.type == "category" || r.type == "tag") || this.query.columns.find(r => r.type == "category" || r.type == "tag");
        const sourceLevelEnabled = tagLevelEnabled && (this.hasCategoryTypeSource('rows') || this.hasCategoryTypeSource('columns'));
        const options = [
          { label: 'Export Articles List', action: "article" },
          { label: 'Export Articles by Tags', action: "tag", disabled: !tagLevelEnabled },
          { label: 'Export Articles by Sources', action: "source", disabled: !sourceLevelEnabled }
        ];

        options.push();

        return options;
      },
      rows() {
        const rows = [];
        const header = [` `];

        if (!get(this.queryResult, 'data.data')) return rows;

        this.queryResult.data.data.forEach((row, i) => {
          const newRow = [];
          newRow.push(row.name);

          row.series.forEach(serie => {
            if (!i) header.push(serie.name)
            newRow.push(serie.value)
          });
          rows.push(newRow);
        });

        rows.unshift(header)

        return rows;
      },

      queryResultHasData () {
        return get(this.queryResult, 'data') !== null && get(this.queryResult, 'data.data') && get(this.queryResult, 'data.data').length > 0;
      }
    },
    methods: {
      getOutputById (id) {
        return this.settings.data.outputs.find(output => output.id === id);
      },
      onArticlesTablePaginationChange (pagination) {
        pagination = pagination.type && pagination.value ? {[pagination.type]: pagination.value} : pagination;
        this.$emit('run', { pagination });
      },
      onQueryTablePaginationChange (pagination) {
        pagination = pagination.type && pagination.value ? {[pagination.type]: pagination.value} : pagination;
        if(this.queryResultPagination) this.$emit('run', { query: { ...this.query, pagination: { ...this.queryResultPagination, ...pagination } } });
      },
      onResultsPaginationChange (type, value) {
        this.$emit('setQueryEntity', { entity: type, value: (value || null) })
      },
      onExport (e, level) {
        this.$emit('export', { formatType: 'csv', level, include_fulltext: this.includeFulltext })
      },
      onRun (runData) {
        this.$emit('run', runData)
      },

      onShowList (showArticleList, cellClicked, pagination) {
        this.$emit('switchListView', { showArticleList, cellClicked, pagination });
      },

      onShowArticleList ({rowHeader, columnHeader}) {
        let cellClicked = {row: rowHeader, column: columnHeader};
        this.onShowList(true, cellClicked)
      },

      onExportToggle () {
        this.showExportOptions = true;
      },

      hasCategoryTypeSource(key) {
        return this.query &&
          this.query[key] &&
          this.query[key].length &&
          this.query[key].some(item => item.category_type_id === CATEGORY_TYPES.SOURCE);
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/results-panel.scss";

  // Need to switch to "Fontawesome" from "Fontawesome Pro" because the animation is off on Pro
  .spinner {
    font: normal normal normal 12px/1 FontAwesome;
    margin-top: 0 !important;

    &:before {
      content: "\f110";
    }

    &:hover {
      color: initial !important;
    }
  }
</style>
